import {useCallback, useEffect, useMemo, useState} from 'react';
import {useRouter} from 'next/router';
import {IBrand} from 'modules/brands/models';
import {PAGINATION_TYPE} from 'plugins/modules/pagination/constants';
import {IPaginationState} from 'plugins/modules/pagination/types';
import {IProductShort} from 'modules/products/types';
import {useGetBrandXQuery} from 'modules/brands/services';
import {ISeo} from 'core/types';
import {extractBrandCodeFromPath} from 'modules/brands/helpers';

interface IUseBrandPaginationProps {
    onControlPageChange: (page: number, isShallow?: boolean) => void;
}

export const useBrandPagination = ({onControlPageChange}: IUseBrandPaginationProps) => {
    const [paginationType, setPaginationType] = useState<IPaginationState['type']>(PAGINATION_TYPE.AUTO);

    const onChange = useCallback(
        (type: IPaginationState['type']) => async (num: number) => {
            setPaginationType(type);

            if (type === PAGINATION_TYPE.AUTO) {
                onControlPageChange(num, false);
                window.scrollTo({behavior: 'auto', left: 0, top: 0});
                return;
            }

            onControlPageChange(num, true);
        },
        [onControlPageChange],
    );

    return {
        paginationType,
        onPageChange: onChange(PAGINATION_TYPE.AUTO),
        onAccPageChange: onChange(PAGINATION_TYPE.ACCUMULATIVE),
    };
};

interface IUseBrandProductsProps {
    products?: IProductShort[];
    paginationType: typeof PAGINATION_TYPE.AUTO | typeof PAGINATION_TYPE.ACCUMULATIVE;
    pageNumber?: number;
}

export const useBrandProducts = ({products, paginationType, pageNumber}: IUseBrandProductsProps) => {
    const [productsData, setProductsData] = useState<IProductShort[]>(products || []);

    useEffect(() => {
        if (!products) {
            return;
        }

        if (paginationType === PAGINATION_TYPE.ACCUMULATIVE) {
            setProductsData((prevProducts) => {
                // Бесконечная догрузка: на первой странице сбрасываем, далее добавляем новые элементы.
                if (1 === pageNumber) {
                    return products;
                }

                return [...prevProducts, ...products];
            });
            return;
        }

        setProductsData(products);
    }, [products, paginationType, pageNumber]);

    return productsData;
};

interface IUseBrandSeoProps {
    brandSeo?: ISeo;
}

export const useBrandSeo = ({brandSeo}: IUseBrandSeoProps) => {
    return useMemo(() => {
        return {
            metaTitle: brandSeo?.metaTitle,
            metaDescription: brandSeo?.metaDescription,
            robots: brandSeo?.robots,
        };
    }, [brandSeo]);
};

export const useBrandX = (): {brand?: IBrand; brandSeo?: ISeo} => {
    const {asPath} = useRouter();
    const code = extractBrandCodeFromPath(asPath);

    const {data: brandsResponse} = useGetBrandXQuery({code});

    const brand = brandsResponse?.data;

    return {
        brandSeo: brandsResponse?.seo,
        brand,
    };
};
